'use client'

import { Box, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

interface CountdownFlashSaleProps {
  endDate: string // ISO string hoặc timestamp
  timeBoxSx?: React.ComponentProps<typeof Box>['sx']
  timeBoxTextProps?: React.ComponentProps<typeof Typography>
  colorDot?: string
  textStyle?: React.ComponentProps<typeof Typography>['sx']
}

interface TimeRemaining {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export default function CountdownFlashSale({
  endDate,
  timeBoxSx,
  timeBoxTextProps,
  colorDot,
  textStyle,
}: CountdownFlashSaleProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  // Tính thời gian còn lại từ hiện tại đến endDate
  const calculateTimeRemaining = useMemo(() => {
    return () => {
      const now = Date.now()
      const end = new Date(endDate).getTime()
      const diff = end - now

      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      return { days, hours, minutes, seconds }
    }
  }, [endDate])

  useEffect(() => {
    // Cập nhật ngay lập tức
    setTimeRemaining(calculateTimeRemaining())

    // Cập nhật mỗi giây
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining()
      setTimeRemaining(remaining)

      // Dừng interval khi hết thời gian
      if (
        remaining.days === 0 &&
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [calculateTimeRemaining])

  return (
    <Stack gap={0.5} alignItems={'flex-end'} sx={textStyle}>
      <Typography fontSize={'14px'} fontWeight={300} className="text-sale">
        Kết thúc sau
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Stack direction="row" spacing={0.5}>
          {timeRemaining.days > 0 && (
            <>
              <TimeBox value={timeRemaining.days} label="d" sx={timeBoxSx} textProps={timeBoxTextProps} />
              <Typography fontSize={'16px'} fontWeight={700} color={colorDot || '#000'}>
                :
              </Typography>
            </>
          )}
          <TimeBox value={timeRemaining.hours} label="h" sx={timeBoxSx} textProps={timeBoxTextProps} />
          <Typography fontSize={'16px'} fontWeight={700} color={colorDot || '#000'}>
            :
          </Typography>
          <TimeBox value={timeRemaining.minutes} label="p" sx={timeBoxSx} textProps={timeBoxTextProps} />
          <Typography fontSize={'16px'} fontWeight={700} color={colorDot || '#000'}>
            :
          </Typography>
          <TimeBox value={timeRemaining.seconds} label="s" sx={timeBoxSx} textProps={timeBoxTextProps} />
        </Stack>
      </Stack>
    </Stack>
  )
}

interface TimeBoxProps {
  value: number
  label: string
  sx?: React.ComponentProps<typeof Box>['sx']
  textProps?: React.ComponentProps<typeof Typography>
}

function TimeBox({ value, label, sx, textProps }: TimeBoxProps) {
  return (
    <Box
      sx={{
        minWidth: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        borderRadius: '4px',
        p: {
          xs: 0.5,
          md: 0.75,
        },
        position: 'relative',
        ...sx,
      }}
    >
      <Typography fontSize={'14px'} fontWeight={700} color="#fff" lineHeight={1} {...textProps}>
        {String(value).padStart(2, '0')}
        {label}
      </Typography>
    </Box>
  )
}
